import React, { useContext, useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from "@material-ui/core";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { getWhatsapp, startWhatsappSession } from "../../services/whatsapp.api";
import { createTelegramUserConnection, sendTelegramUserPassword } from "../../services/telegram.api";

/**
 * Alta de una cuenta PERSONAL de Telegram (doc 0438 Parte V, TU5): como API Baileys, por QR.
 *
 * El QR lo publica el backend en `Whatsapps.qrcode` y llega por los MISMOS eventos de socket que
 * el de WhatsApp. Si la cuenta tiene verificación en dos pasos, la contraseña se pide aquí y viaja
 * una sola vez: el backend se la entrega a Telegram y no la guarda.
 */

const MOTIVOS = {
  QR_EXPIRED: "El QR caducó sin escanearse. Genera uno nuevo.",
  SESSION_REVOKED: "La sesión se cerró desde el móvil. Vuelve a vincular.",
  PASSWORD_HASH_INVALID: "Contraseña incorrecta. Inténtalo de nuevo.",
  ERR_TELEGRAM_ACCOUNT_IN_USE: "Esa cuenta de Telegram ya está conectada en otra conexión.",
};

const TelegramUserConnectionModal = ({ open, onClose, onSave }) => {
  const { user, socket } = useContext(AuthContext);
  const [name, setName] = useState("");
  const [acepto, setAcepto] = useState(false);
  const [creando, setCreando] = useState(false);
  const [conexion, setConexion] = useState(null);
  const [estado, setEstado] = useState(null);
  const [qr, setQr] = useState("");
  const [motivo, setMotivo] = useState("");
  const [clave, setClave] = useState("");
  const [enviandoClave, setEnviandoClave] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName("");
    setAcepto(false);
    setConexion(null);
    setEstado(null);
    setQr("");
    setMotivo("");
    setClave("");
  }, [open]);

  useEffect(() => {
    if (!conexion || !socket || !user?.companyId) return undefined;
    const aplicar = (w, error) => {
      if (!w || w.id !== conexion) return;
      setEstado(w.status);
      setQr(w.status === "qrcode" ? w.qrcode || "" : "");
      if (error?.code) setMotivo(MOTIVOS[error.code] || `Telegram respondió: ${error.code}`);
      if (w.status === "qrcode") setMotivo("");
      if (w.status === "CONNECTED") {
        toast.success(`Telegram conectado${w.number ? `: ${w.number}` : ""}`);
        cerrar();
      }
    };
    const alEvento = (data) => {
      if (data?.action !== "update") return;
      aplicar(data.session || data.whatsapp, data.error);
    };
    socket.on(`company-${user.companyId}-whatsappSession`, alEvento);
    socket.on(`company-${user.companyId}-whatsapp`, alEvento);
    // El primer QR puede haberse publicado antes de suscribirse.
    getWhatsapp(conexion).then(({ data }) => aplicar(data)).catch(() => undefined);
    return () => {
      socket.off(`company-${user.companyId}-whatsappSession`, alEvento);
      socket.off(`company-${user.companyId}-whatsapp`, alEvento);
    };
  }, [conexion, socket, user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Con una conexión ya creada, cerrar = refrescar la lista (onSave también cierra el modal).
  const cerrar = () => (conexion && onSave ? onSave() : onClose());

  const crear = async () => {
    setCreando(true);
    try {
      const { data } = await createTelegramUserConnection({ name: name.trim(), acceptResponsibility: acepto });
      setConexion(data.id);
      setEstado("OPENING");
    } catch (err) {
      toastError(err);
    } finally {
      setCreando(false);
    }
  };

  const otroQr = async () => {
    setMotivo("");
    setEstado("OPENING");
    try {
      await startWhatsappSession(conexion);
    } catch (err) {
      toastError(err);
    }
  };

  const enviarClave = async () => {
    setEnviandoClave(true);
    setMotivo("");
    try {
      await sendTelegramUserPassword(conexion, clave);
      setClave("");
      setEstado("OPENING");
    } catch (err) {
      toastError(err);
    } finally {
      setEnviandoClave(false);
    }
  };

  const paso1 = (
    <>
      <Typography variant="body2" gutterBottom>
        Conecta <b>tu cuenta de Telegram</b>, como en WhatsApp: escaneas un QR y llegan al panel tus chats
        privados y tus grupos. No hace falta ningún bot.
      </Typography>
      <TextField
        label="Nombre de la conexión"
        variant="outlined"
        size="small"
        fullWidth
        autoFocus
        margin="dense"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Ej.: Telegram ventas"
      />
      <Box mt={2} p={1.5} style={{ background: "rgba(255, 152, 0, 0.08)", borderRadius: 6 }}>
        <Typography variant="body2" gutterBottom>
          <b>Aviso.</b> Vas a conectar tu cuenta personal a una aplicación que no es de Telegram. Telegram
          vigila estas cuentas y puede restringirlas si detecta spam o un uso abusivo. <b>El uso que hagas de
          tu cuenta es responsabilidad tuya.</b>
        </Typography>
        <FormControlLabel
          control={<Checkbox color="primary" checked={acepto} onChange={(e) => setAcepto(e.target.checked)} />}
          label={<Typography variant="body2">Lo entiendo y acepto la responsabilidad</Typography>}
        />
      </Box>
    </>
  );

  const paso2 = (
    <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
      {estado === "PASSWORD_REQUIRED" ? (
        <>
          <Typography variant="body2" gutterBottom>
            Tu cuenta tiene <b>verificación en dos pasos</b>. Escribe la contraseña que usas en Telegram:
          </Typography>
          <TextField
            type="password"
            label="Contraseña"
            variant="outlined"
            size="small"
            fullWidth
            autoFocus
            margin="dense"
            value={clave}
            onChange={(e) => setClave(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && clave && enviarClave()}
            helperText="Se entrega a Telegram y no se guarda"
          />
        </>
      ) : qr ? (
        <>
          <Typography variant="body2" gutterBottom>
            En el móvil: <b>Telegram → Ajustes → Dispositivos → Vincular dispositivo de escritorio</b>, y escanea:
          </Typography>
          <Box p={2} bgcolor="#fff" borderRadius={8} mt={1}>
            <QRCodeSVG value={qr} size={240} />
          </Box>
          <Typography variant="caption" color="textSecondary" style={{ marginTop: 8 }}>
            El código se renueva solo cada pocos segundos.
          </Typography>
        </>
      ) : estado === "DISCONNECTED" ? (
        <Typography variant="body2">{motivo || "La vinculación se detuvo."}</Typography>
      ) : (
        <Box display="flex" alignItems="center" py={4}>
          <CircularProgress size={22} />
          <Typography variant="body2" style={{ marginLeft: 12 }}>
            Preparando el QR…
          </Typography>
        </Box>
      )}
      {motivo && estado !== "DISCONNECTED" && (
        <Typography variant="body2" color="error" style={{ marginTop: 8 }}>
          {motivo}
        </Typography>
      )}
    </Box>
  );

  return (
    <Dialog open={open} onClose={cerrar} maxWidth="xs" fullWidth>
      <DialogTitle>Conectar tu cuenta de Telegram</DialogTitle>
      <DialogContent dividers>{conexion ? paso2 : paso1}</DialogContent>
      <DialogActions>
        <Button onClick={cerrar}>{conexion ? "Cerrar" : "Cancelar"}</Button>
        {!conexion && (
          <Button color="primary" variant="contained" disabled={!name.trim() || !acepto || creando} onClick={crear}>
            {creando ? <CircularProgress size={18} /> : "Generar QR"}
          </Button>
        )}
        {conexion && estado === "PASSWORD_REQUIRED" && (
          <Button color="primary" variant="contained" disabled={!clave || enviandoClave} onClick={enviarClave}>
            {enviandoClave ? <CircularProgress size={18} /> : "Enviar"}
          </Button>
        )}
        {conexion && estado === "DISCONNECTED" && (
          <Button color="primary" variant="contained" onClick={otroQr}>
            Generar otro QR
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TelegramUserConnectionModal;
